import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs/internal/Subscription';

import { AuthService } from '../modules/authentication/services/auth.service';
import { CommonService } from './common.service';
import { navData } from '../data/nav-data';

enum ContentTypesEnum {
  notifications = 'NOTIFICATIONS',
  tooltips = 'TOOLTIPS',
}

export type MisContent = 'NOTIFICATIONS' | 'TOOLTIPS' | '';

export interface ContentTypes {
  notifications?: MisContent;
  tooltips?: MisContent;
}

@Injectable({ providedIn: 'root' })
export class CustomContentService {
  public contentAdminDialogSettings: any;
  public notificationViewDialogSettings: any;
  public defaultLanguage: { country: string; shortCode: string } = { country: 'english', shortCode: 'en' };

  pageTooltips: Array<any>;
  contentTypes: ContentTypes = {
    notifications: ContentTypesEnum.notifications,
    tooltips: ContentTypesEnum.tooltips,
  };
  acknowledgeableContent: Array<MisContent> = [ContentTypesEnum.notifications];
  currentContentType: MisContent = '';

  subscriptions: { [key: string]: Subscription } = {
    navReady: null,
    pageChanged: null,
  };

  constructor(public cService: CommonService, private authService: AuthService, public dialog: MatDialog) {
    this.contentAdminDialogSettings = {
      maxHeight: this.cService.isMobileViewport ? '100%' : '',
      minWidth: this.cService.isMobileViewport ? '100%' : '950px',
      width: this.cService.isMobileViewport ? '100%' : '60vw',
      disableClose: true,
    };

    this.notificationViewDialogSettings = {
      width: '600px',
      disableClose: true,
    };

    this.subscriptions.navReady = this.cService.navigationReady$.subscribe({
      next: () => {
        if (this.authService.isAuthenticated) {
          this.getAndCacheTooltips(null, false, () => {
            this.assignApplicablePageTooltips();
          });
        }
      },
    });
  }

  assignApplicablePageTooltips(): void {
    const tooltipInPreviewMode = localStorage.getItem(this.cService.localStorageKeys.pageTooltipPreviews);

    if (this.cService.navigation.length && this.pageTooltips.length) {
      this.cService.navigation.forEach((navigation) => {
        let pageTooltips: Array<any> = [];

        if (tooltipInPreviewMode) {
          pageTooltips = this.pageTooltips.filter(
            (pTip: { menuID: string; tooltipStatus: string }) =>
              pTip.menuID === navigation.id &&
              (pTip.tooltipStatus.toLowerCase() === 'active' || pTip.tooltipStatus.toLowerCase() === 'pending')
          );
        } else {
          pageTooltips = this.pageTooltips.filter(
            (pTip: { menuID: string; tooltipStatus: string }) =>
              pTip.menuID === navigation.id && pTip.tooltipStatus.toLowerCase() === 'active'
          );
        }

        if (pageTooltips && pageTooltips.length) {
          const selectLast = pageTooltips.length - 1;

          setTimeout(() => {
            navigation.pageTip = pageTooltips.filter((pTip: { onTitle: boolean }) => pTip.onTitle)[selectLast];
            navigation.pageNavTip = pageTooltips.filter((pTip: { onMenu: boolean }) => pTip.onMenu)[selectLast];
          });
        }

        if (navigation.children && navigation.children.length) {
          navigation.children.forEach((childNav) => {
            let childPageTooltips: Array<any> = [];

            if (tooltipInPreviewMode) {
              childPageTooltips = this.pageTooltips.filter(
                (cpTip: { menuID: string; tooltipStatus: string }) =>
                  cpTip.menuID === childNav.id &&
                  (cpTip.tooltipStatus.toLowerCase() === 'active' || cpTip.tooltipStatus.toLowerCase() === 'pending')
              );
            } else {
              childPageTooltips = this.pageTooltips.filter(
                (cpTip: { menuID: string; tooltipStatus: string }) =>
                  cpTip.menuID === childNav.id && cpTip.tooltipStatus.toLowerCase() === 'active'
              );
            }

            if (childPageTooltips && childPageTooltips.length) {
              const selectLast = childPageTooltips.length - 1;

              setTimeout(() => {
                childNav.pageTip = childPageTooltips.filter((cpTip: { onTitle: boolean }) => cpTip.onTitle)[selectLast];
                childNav.pageNavTip = childPageTooltips.filter((cpTip: { onMenu: boolean }) => cpTip.onMenu)[
                  selectLast
                ];
              });
            }
          });
        }
      });
    }

    if (tooltipInPreviewMode) {
      console.log('%cTOOLTIP IN PREVIEW MODE!', 'color: limegreen');
      localStorage.removeItem(this.cService.localStorageKeys.pageTooltipPreviews);
    }
  }

  public getNotifications() {
    return this.cService.commonGet('reports/custom_content/notifications/display');
  }

  getAndCacheTooltips(refreshedData?: Array<any>, updateCacheOnly: boolean = false, callback?: () => void) {
    if (this.authService.isAuthenticated()) {
      if (updateCacheOnly) {
        this.pageTooltips = refreshedData;
        if (callback) {
          callback();
        }
      } else {
        this.cService.commonGet('reports/custom_content/tooltips').subscribe({
          next: (response: { data: Array<any> }) => {
            console.log('%cPAGE TOOLTIPS', 'color: lime', response.data);

            if (response.data) {
              this.pageTooltips = response.data;
              if (callback) {
                callback();
              }
            }
          },
        });
      }
    }
  }

  public getCompanyLocationFiltersForAudience(company) {
    return this.cService.commonGet('reports/custom_content/notifications/get_locations_filter/' + company);
  }

  public getContent(messageID: string) {
    return this.cService.commonGet('reports/custom_content/' + messageID);
  }

  public saveContent(notificationData: any) {
    return this.cService.commonPost('/reports/custom_content/save', notificationData);
  }

  public updateContent(id: string, notificationData: any) {
    return this.cService.commonPut('/reports/custom_content/update/' + id, notificationData);
  }

  public deleteContent(messageID: string) {
    return this.cService.commonDelete('reports/custom_content/' + messageID);
  }

  public acknowledgeNotification(messageID) {
    this.cService.commonPost('reports/custom_content/notifications/acknowledge', { messageID: messageID }).subscribe({
      next: (response: any) => {},
    });
  }

  public getLanguageShortCode(language) {
    return this.cService.getLanguageByCountry(language).code;
  }

  public updateMenuBadgeCount() {
    return this.cService.triggerMenuBadgeCountUpdateObservable(navData.misNotifications.idName);
  }

  public deactivateContent(messageID) {
    return this.cService.commonPatch('reports/custom_content/notifications/deactivate', { messageID: messageID });
  }

  public activateContent(messageID) {
    return this.cService.commonPatch('reports/custom_content/notifications/activate', { messageID: messageID });
  }
}
